import { Api } from "@/api";
import AddressCard from "@/components/AddressCard/AddressCard";
import {
  CA_PROVINCES,
  CheckoutContext,
  SHIP_COUNTRIES,
  ShippingOption,
  ShippingType,
  US_STATES,
  isValidCAPostalFormat,
  isValidCAProvince,
  isValidPostalForProvince,
  isValidUSState,
  isValidZipForState,
} from "@/components/CheckoutForm/utils";
import { CheckoutSelect } from "@/components/CheckoutSelect/CheckoutSelect";
import { CheckoutTextInput } from "@/components/CheckoutTextInput/CheckoutTextInput";
import DescriptiveRadioGroup from "@/components/DescriptiveRadioGroup/DescriptiveRadioGroup";
import InputRadioGroup from "@/components/InputRadioGroup/InputRadioGroup";
import { hasMultipleCalculatedItemsByDimensions } from "@/components/CheckoutForm/multipleCalculatedHeuristic";
import { TRIBECA_ADDRESS } from "@/utils/constants";
import { formatStripePrice } from "@/utils/functions/formatPrice";
import { usePending } from "@/utils/hooks/usePending.js";
import { Button, Tooltip } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "@mantine/hooks";

// ── Phase A address validation (2026-07-23, Decision 1: suggest-and-allow;
// hard-block ONLY a state/ZIP contradiction; fail-open when the validator
// is unreachable). Server route: /api/address-validation/validate
// (Google-backed — see functions/api/addressValidation.ts in the server repo).
type AddressSuggestion = {
  street: string;
  apartment?: string;
  city: string;
  state: string;
  zipcode: string;
  country?: string;
};
type AddressCheck = {
  status: "verified" | "corrected" | "unverified" | "error";
  suggested?: AddressSuggestion;
  messages: string[];
  stateZipMismatch: boolean;
};
type AddressReviewState = AddressCheck & {
  key: string;
  target: "billing" | "shipping" | "delivery";
  diffs?: ("street" | "state" | "zip")[];
};
const streetNumberOf = (street?: string) =>
  (String(street || "").trim().match(/^\d+[A-Za-z]?/) || [""])[0].toUpperCase();
const zip5Of = (zip?: string, country?: string) =>
  String(country || "US").toUpperCase() === "CA"
    ? String(zip || "").replace(/\s+/g, "").toUpperCase()
    : String(zip || "").substring(0, 5);
// What actually changed between typed and suggested — empty = cosmetic.
const addressDiffs = (
  typed: { street?: string; state?: string; zipcode?: string; country?: string },
  suggested: { street?: string; state?: string; zipcode?: string; country?: string }
): ("street" | "state" | "zip")[] => {
  const out: ("street" | "state" | "zip")[] = [];
  if (streetNumberOf(typed.street) !== streetNumberOf(suggested.street)) out.push("street");
  if (
    String(typed.state || "").trim().toUpperCase() !==
    String(suggested.state || "").trim().toUpperCase()
  )
    out.push("state");
  if (zip5Of(typed.zipcode, typed.country) !== zip5Of(suggested.zipcode, suggested.country))
    out.push("zip");
  return out;
};
const addressKeyOf = (
  street?: string,
  apartment?: string,
  city?: string,
  state?: string,
  zipcode?: string,
  country?: string
) =>
  JSON.stringify([
    street || "",
    apartment || "",
    city || "",
    state || "",
    zipcode || "",
    (country || "US").toUpperCase(),
  ]);

interface CheckoutShippingFormProps {
  ctx: CheckoutContext;
  onNext: () => void;
  onPrev: () => void;
  isPanelOpen: boolean;
}

const SHIPPING_RECALC_DEBOUNCE_MS = 800;

export const CheckoutShippingForm = ({
  ctx,
  onNext,
  onPrev,
  isPanelOpen,
}: CheckoutShippingFormProps) => {
  const {
    checkout,
    data,
    setData,
    shippingOptions,
    shippingPickupOption,
    shippingOversizedOption,
    shippingTypes,
    setIsUpdatingPricing,
    calculateShippingPrices,
    cartSubtotal,
    isCalculatingShipping,
    shippingStatus,
  } = ctx;
  const [showValidation, setShowValidation] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  // Must-choose delivery (2026-07-18): visual only — the backend default
  // shippingOption is never cleared, so Stripe always has a valid rate.
  const [hasUserChosenDelivery, setHasUserChosenDelivery] = useState(false);
  // Phase A address validation state (2026-07-23).
  const [addressReview, setAddressReview] = useState<AddressReviewState | null>(
    null
  );
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  // 2026-10-03 (Michael): a cosmetic postal correction (same street number,
  // state and ZIP5 — e.g. "81st Street" → "81st St") is applied silently and
  // Save & Continue advances. Only a REAL difference (street number, state
  // or ZIP) stops the customer; a second click on Save & Continue while that
  // card is up flashes it red so they know the card is what's blocking.
  const [addressAttention, setAddressAttention] = useState(false);
  const addressReviewRef = useRef<HTMLDivElement>(null);
  const approvedAddressKeyRef = useRef("");
  const sentinelRef = useRef<HTMLDivElement>(null);
  const deliveryOptionsRef = useRef<HTMLParagraphElement>(null);
  const hasRevealedOptionsRef = useRef(false);
  const checkoutRef = useRef(checkout);
  const setIsUpdatingPricingRef = useRef(setIsUpdatingPricing);
  // FIX 2026-07-24 (snap-back): the latch/restore choreography below must
  // never steal an explicit customer click. Remember the last ship-to type
  // the CUSTOMER chose (radio click) and which type an auto-latch displaced,
  // so every restore returns to the customer's choice — never a hard-coded
  // "shipToMyAddress".
  const lastExplicitShipToRef =
    useRef<ShippingType["value"]>("shipToMyAddress");
  const autoLatchedFromRef = useRef<ShippingType["value"] | null>(null);
  const isPickup = data.shippingType === "pickup";
  const exceedsShippingLimits = shippingStatus === "exceeds_table";
  const skipShipping = Boolean((ctx.cart as any)?.skipShipping);
  // 10-19 (T-51898_B): a cart with nothing shippable (only Shipping / Scan /
  // courier lines) is "custom shipping" by default — the backend answers
  // `custom_shipping` with the custom marker rate as the only option. Unlike
  // the cart-level switch, the destination IS collected (ship-to / pickup
  // radios stay), so tax follows the address the customer enters; the
  // delivery block shows the "Custom" tile instead of a radio list.
  const customOnly = !skipShipping && shippingStatus === "custom_shipping";
  // Shipping-only cart (2026-10-05): $0 copies of an already-paid order's
  // pieces — the customer is here to SHIP them, so Pickup is not offered and
  // the default (pickup) selection is moved to ship-to-billing on arrival.
  const shippingOnlyFor = String(
    (ctx.cart as any)?.shippingOnlyForOrderNo || ""
  ).trim();
  const shippingOnly = Boolean(shippingOnlyFor);
  const isMobile = useMediaQuery("(max-width: 767px)");
  // Phase B follow-on (2026-07-24): billing country is worldwide, but we
  // only SHIP to US + CA. When the billing country is anything else,
  // ship-to-my-billing-address can never be fulfilled — suppress the priced
  // options (a 750xx French postal code would otherwise price as a Texas
  // ZIP) and say so inline instead of only on submit (found in 7/24 test).
  // Declared up here: shouldShowDeliveryOptions below reads it (TS2448 on
  // the first attempt — deploy 12:59 PM failed the build, nothing shipped).
  const billingCountryCode = String(data.billingCountry || "US")
    .trim()
    .toUpperCase();
  const billingShippable = ["US", "CA"].includes(billingCountryCode);

  // PIN #6 (Michael 2026-07-19 — wording APPROVED): HI/AK destinations ride
  // the oversized/exceeds status for the arranged-delivery gate, so the
  // delivery radio chip wrongly read the "piece too large" oversized copy.
  // HI/AK is destination-based ("treated like international"), not a size
  // problem. InputRadioGroup renders each option's own label/tooltipText, so
  // for an HI/AK destination we pass our own chip option with the approved
  // copy instead of letting the internal oversized branch fire. Genuine
  // oversized copy is untouched everywhere else.
  const isHiAkState = (state?: string) =>
    ["HI", "AK"].includes(
      String(state || "")
        .trim()
        .toUpperCase()
    );
  const isHiAkZip = (zip?: string) => {
    const z = parseInt(String(zip || "").substring(0, 5));
    if (!Number.isFinite(z)) return false;
    return (z >= 96700 && z <= 96999) || (z >= 99500 && z <= 99999);
  };
  // Destination precedence mirrors the quote-state address source (delivery
  // fields), falling back to the address that triggered the gate before the
  // switch to the delivery form.
  // Phase B (2026-07-23): HI/AK is a US concept — a CA destination must
  // never trip this gate (CA has its own arranged-delivery routing on the
  // server side).
  const isHiAkDestination = (() => {
    if (data.deliveryState?.trim() || data.deliveryZipcode?.trim()) {
      if ((data.deliveryCountry || "US") !== "US") return false;
      return isHiAkState(data.deliveryState) || isHiAkZip(data.deliveryZipcode);
    }
    if (data.shippingState?.trim() || data.shippingZipcode?.trim()) {
      if ((data.shippingCountry || "US") !== "US") return false;
      return isHiAkState(data.shippingState) || isHiAkZip(data.shippingZipcode);
    }
    if ((data.billingCountry || "US") !== "US") return false;
    return isHiAkState(data.billingState) || isHiAkZip(data.billingZipcode);
  })();
  // FIX 2026-07-23 v2: a state/ZIP CONTRADICTION (State=HI + ZIP=98103) must
  // never drive live UI. The server prices by ZIP while this gate keys on
  // state, so during a contradiction the customer saw a priced UPS option
  // AND "arranged after order" copy at once. While the active destination's
  // state and ZIP disagree, suppress the HI/AK chip AND the delivery
  // options (below) — the red ZIP field explains why, and the submit
  // hard-block backs it up. Same field precedence as isHiAkDestination.
  // Phase B: country-aware — CA contradicts on province vs postal first
  // letter (mirrors the server check); US unchanged.
  const contradictedPair = (state?: string, zip?: string, country?: string) => {
    const s = String(state || "").trim();
    if (!s) return false;
    if ((country || "US") === "CA") {
      const p = String(zip || "").trim();
      if (!p) return false;
      return !isValidPostalForProvince(p, s);
    }
    const z = String(zip || "").replace(/\D/g, "");
    if (z.length < 5) return false;
    return !isValidZipForState(z, s);
  };
  const destinationContradicted = (() => {
    if (data.deliveryState?.trim() || data.deliveryZipcode?.trim()) {
      return contradictedPair(
        data.deliveryState,
        data.deliveryZipcode,
        data.deliveryCountry
      );
    }
    if (data.shippingState?.trim() || data.shippingZipcode?.trim()) {
      return contradictedPair(
        data.shippingState,
        data.shippingZipcode,
        data.shippingCountry
      );
    }
    return contradictedPair(
      data.billingState,
      data.billingZipcode,
      data.billingCountry
    );
  })();
  const isHiAkArrangedDelivery =
    exceedsShippingLimits && isHiAkDestination && !destinationContradicted;

  useEffect(() => {
    checkoutRef.current = checkout;
  }, [checkout]);

  // Carts with custom (skipped) shipping have exactly one shipping rate (the
  // "custom" rate) and collect no shipping address. Select that rate and
  // silently use the studio address so Stripe can confirm the session — it is
  // never displayed and, since automatic tax is disabled, has no tax effect.
  useEffect(() => {
    if (!skipShipping) return;
    const customOption = shippingOptions[0]?.value;
    (async () => {
      setIsUpdatingPricing(true);
      try {
        await checkout.updateShippingAddress({
          name: TRIBECA_ADDRESS.name,
          address: TRIBECA_ADDRESS.address,
        });
        if (customOption) {
          if (data.shippingOption !== customOption) {
            setData({ ...data, shippingOption: customOption });
          }
          await checkout.updateShippingOption(customOption);
        }
      } catch (error) {
        console.error("❌ Failed to set custom shipping:", error);
      } finally {
        setIsUpdatingPricing(false);
      }
    })();
  }, [skipShipping, shippingOptions]);

  useEffect(() => {
    setIsUpdatingPricingRef.current = setIsUpdatingPricing;
  }, [setIsUpdatingPricing]);

  const hasMultipleCalculatedItems = useMemo(() => {
    // Primary: backend confirmed multiple items have positive calculated shipping
    if (shippingStatus === "multiple_calculated") return true;

    // FIX 2026-07-20 (PIN #12a): the dimension heuristic is a PRE-CALCULATION
    // stopgap only (first render, before a destination exists). Once the
    // backend has resolved a real status for this cart ("calculated",
    // "free_shipping", "oversize_calculated", ...) its answer WINS — the old
    // code re-ran the heuristic on every render and overrode priced results
    // (real mat-only carts showed "UPS — quoted after order" while the API
    // returned a priced UPS rate). "standard" is the stored pre-calculation
    // default, so it does NOT count as resolved.
    if (shippingStatus && shippingStatus !== "standard") return false;
    if (exceedsShippingLimits) return false;
    return hasMultipleCalculatedItemsByDimensions(ctx.cart);
  }, [shippingStatus, ctx.cart, exceedsShippingLimits]);

  useEffect(() => {
    if (!shippingOnly || isCalculatingShipping) return;
    if (data.shippingType === "pickup") updateShippingType("shipToMyAddress");
  }, [shippingOnly, data.shippingType, isCalculatingShipping]);

  useEffect(() => {
    if (skipShipping) return;
    // FIX 2026-07-24: act on SETTLED status only — an in-flight recalc's
    // transient status must never rewrite the customer's radio selection.
    if (isCalculatingShipping) return;
    const isShipToAddressType =
      data.shippingType === "shipToMyAddress" ||
      data.shippingType === "shipToOtherAddress";
    if (exceedsShippingLimits && isShipToAddressType) {
      autoLatchedFromRef.current = data.shippingType;
      updateShippingType("oversized");
    }
  }, [exceedsShippingLimits, data.shippingType, isCalculatingShipping]);

  useEffect(() => {
    if (skipShipping) return;
    // FIX 2026-07-24: settled status only (see the oversized latch above).
    if (isCalculatingShipping) return;
    if (exceedsShippingLimits) return;

    const isShipToAddressType =
      data.shippingType === "shipToMyAddress" ||
      data.shippingType === "shipToOtherAddress";

    // Keep explicit pickup selection as-is, but normalize ship-to selections
    // to Delivery when multi-calculated eligibility appears after cart updates.
    if (hasMultipleCalculatedItems && isShipToAddressType) {
      autoLatchedFromRef.current = data.shippingType;
      updateShippingType("multiple-calculated");
      return;
    }

    // When multi-calculated eligibility is lost, move back to a normal
    // shipping flow to keep radio selection and address form aligned.
    // FIX 2026-07-24: restore the type this latch displaced (or the last
    // explicit customer click) — the old hard-coded "shipToMyAddress"
    // stole the customer's selection.
    if (
      !hasMultipleCalculatedItems &&
      data.shippingType === "multiple-calculated"
    ) {
      const restoreTo =
        autoLatchedFromRef.current ?? lastExplicitShipToRef.current;
      autoLatchedFromRef.current = null;
      updateShippingType(restoreTo);
    }
  }, [
    exceedsShippingLimits,
    hasMultipleCalculatedItems,
    data.shippingType,
    isCalculatingShipping,
  ]);

  // FIX 2026-07-24: symmetric un-latch for the oversized path — when the
  // exceeds status that displaced a ship-to selection clears (address or
  // cart edit), return to the type the latch displaced. Carts that arrive
  // in the oversized state without a displaced click are untouched.
  useEffect(() => {
    if (skipShipping) return;
    if (isCalculatingShipping) return;
    if (exceedsShippingLimits) return;
    if (data.shippingType !== "oversized") return;
    if (!autoLatchedFromRef.current) return;
    const restoreTo = autoLatchedFromRef.current;
    autoLatchedFromRef.current = null;
    updateShippingType(restoreTo);
  }, [exceedsShippingLimits, data.shippingType, isCalculatingShipping]);

  useEffect(() => {
    setHasScrolledToBottom(false);
    let observer: IntersectionObserver | null = null;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setHasScrolledToBottom(true);
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -80px 0px",
      }
    );
    observer.observe(sentinel);

    return () => {
      observer?.disconnect();
    };
  }, [data.shippingType]);

  // 10-12: rates are "pending" from the moment the address changes — the
  // recalc below is debounced, and isCalculatingShipping only turns on when it
  // starts, so the previous (pre-address grid) price flashed for that gap.
  const [ratesPending, setRatesPending] = useState(false);
  // Calculate shipping when address changes and shipping type is not pickup
  useEffect(() => {
    if (!(!isPickup && !skipShipping && calculateShippingPrices)) {
      setRatesPending(false);
    }
    if (!isPickup && !skipShipping && calculateShippingPrices) {
      setRatesPending(true);
      const timeoutId = setTimeout(() => {
        (async () => {
          setIsUpdatingPricingRef.current(true);
          try {
            if (
              data.shippingType === "shipToOtherAddress" &&
              data.shippingStreet?.trim() &&
              data.shippingCity?.trim() &&
              data.shippingState?.trim() &&
              data.shippingZipcode?.trim()
            ) {
              await checkoutRef.current.updateShippingAddress({
                name: `${data.shippingFirstName} ${data.shippingLastName}`,
                address: {
                  line1: data.shippingStreet.trim(),
                  line2: data.shippingApartment || "",
                  city: data.shippingCity.trim(),
                  state: data.shippingState,
                  postal_code: data.shippingZipcode,
                  country: data.shippingCountry || "US",
                },
              });
            }
            await calculateShippingPrices();
          } finally {
            setIsUpdatingPricingRef.current(false);
            setRatesPending(false);
          }
        })();
      }, SHIPPING_RECALC_DEBOUNCE_MS);

      return () => clearTimeout(timeoutId);
    }
  }, [
    data.billingZipcode,
    data.shippingApartment,
    data.shippingCity,
    data.shippingFirstName,
    data.shippingLastName,
    data.shippingState,
    data.shippingStreet,
    data.shippingZipcode,
    data.shippingCountry,
    data.shippingType,
    isPickup,
    calculateShippingPrices,
    cartSubtotal,
  ]);

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  const updateShippingType = usePending(async function (
    shippingType: ShippingType["value"]
  ) {
    try {
      setIsUpdatingPricing(true);

      const shippingOption =
        shippingType === "oversized" || shippingType === "multiple-calculated"
          ? shippingOversizedOption?.value || shippingPickupOption.value
          : shippingType === "pickup"
            ? shippingPickupOption.value
            : shippingOptions[0]?.value || "";
      setData({
        ...data,
        shippingType,
        shippingOption,
        billingAddressType: "shipping-address",
      });

      if (
        shippingType === "pickup" ||
        shippingType === "oversized" ||
        shippingType === "multiple-calculated"
      ) {
        await checkout.updateShippingAddress({
          name: TRIBECA_ADDRESS.name,
          address: TRIBECA_ADDRESS.address,
        });
        await checkout.updateShippingOption(shippingOption);
      } else if (shippingType === "shipToMyAddress") {
        await checkout.updateShippingAddress({
          name: `${data.firstName} ${data.lastName}`,
          address: {
            line1: data.billingStreet.trim(),
            line2: data.billingApartment || "",
            city: data.billingCity.trim(),
            state: data.billingState,
            postal_code: data.billingZipcode,
            country: data.billingCountry || "US",
          },
        });

        await checkout.updateShippingOption(shippingOption);
      } else if (shippingType === "shipToOtherAddress") {
        await checkout.updateShippingAddress({
          name: `${data.shippingFirstName} ${data.shippingLastName}`,
          address: {
            line1: data.shippingStreet.trim(),
            line2: data.shippingApartment || "",
            city: data.shippingCity.trim(),
            state: data.shippingState,
            postal_code: data.shippingZipcode,
            country: data.shippingCountry || "US",
          },
        });

        // Update shipping option after address is set
        await checkout.updateShippingOption(shippingOption);
      }
    } catch (error) {
      console.error("❌ updateShippingType failed:", error);
    } finally {
      setIsUpdatingPricing(false);
    }
  });

  const shouldShowZipcode = (name: string) => {
    return name.toLowerCase().includes("local");
  };

  const deliveryOptions = useMemo(() => {
    const options = [];
    const isPickupType = data.shippingType === "pickup";
    const isMultipleCalculatedType =
      data.shippingType === "multiple-calculated";
    // 2026-07-18: the oversized (exceeds-table) state gets the SAME treatment
    // as F1's multiple-calculated — the backend deliberately offers the
    // messenger alongside arranged delivery (messenger has no UPS box
    // ceiling), but this list previously never rendered it.
    const isOversizedType = data.shippingType === "oversized";
    const isQuoteArrangedType = isMultipleCalculatedType || isOversizedType;
    const isShippingType =
      data.shippingType === "shipToMyAddress" ||
      data.shippingType === "shipToOtherAddress";

    const pickupDisabled = isShippingType || isQuoteArrangedType;

    // F1: in the quote-arranged states only the messenger is genuinely
    // selectable (UPS/oversized is contact-for-quote and pickup has its own
    // radio), so hide pickup from this list rather than showing it disabled.
    if (!isQuoteArrangedType) {
      options.push({
        title: shippingPickupOption.display_name || "Pick up in studio",
        zipcode: false,
        price: formatStripePrice(shippingPickupOption.price || 0),
        value: shippingPickupOption.value,
        disabled: pickupDisabled,
      });
    }

    shippingOptions.forEach(option => {
      if (option.value !== shippingPickupOption.value) {
        // F1 + oversized: quote-arranged states offer ONLY the messenger as
        // a priced, selectable method; any other resolved method is
        // suppressed there.
        if (isQuoteArrangedType && option.label !== "manhattan-messenger") {
          return;
        }
        const finalPrice = option.price || 0;
        const formattedPrice = formatStripePrice(finalPrice);
        const shippingDisabled = isPickupType;

        options.push({
          title: option.display_name || option.label,
          zipcode: shouldShowZipcode(option.label),
          price: formattedPrice,
          value: option.value,
          disabled: shippingDisabled,
          optionKey: option.label,
        });
      }
    });

    // F1: explicit way back to the contact-for-quote flow (multiple-calculated
    // only). On the OVERSIZED path with a messenger available, the messenger
    // IS the delivery (Michael 7/18) — no quote option is shown; with no
    // messenger the whole options list stays hidden (arranged-delivery copy).
    const oversizedHasMessenger =
      isOversizedType &&
      shippingOptions.some(option => option.label === "manhattan-messenger");
    if (isMultipleCalculatedType || (isOversizedType && !oversizedHasMessenger)) {
      options.push({
        title: isMultipleCalculatedType
          ? "UPS — we'll contact you with a delivery quote"
          : "Oversized delivery — we'll contact you with a delivery quote",
        zipcode: false,
        price: "Quoted after order",
        value: shippingOversizedOption?.value || shippingPickupOption.value,
        disabled: false,
        // PIN #3: mark the contact-for-quote entry so the default-selection
        // rule below can tell it apart from real-priced options.
        isQuote: true,
      });
    }

    return options;
  }, [
    shippingOptions,
    shippingPickupOption,
    shippingOversizedOption,
    data.shippingType,
  ]);

  // Only force an explicit choice when the customer actually has one: 2+
  // enabled, non-pickup delivery options (Michael 2026-07-18). A single
  // option stays pre-selected as before — zero added friction.
  // 2026-10-01 (Michael): UPS expedited upgrades (rate keys "expedite-*")
  // do NOT count as a "real choice" — Ground stays pre-selected and the
  // faster services are optional upgrades. The must-choose gate still fires
  // for UPS-vs-messenger and the other genuine either/or cases.
  const selectableDeliveryCount = deliveryOptions.filter(
    option =>
      !option.disabled &&
      option.value !== shippingPickupOption.value &&
      !String((option as { optionKey?: string }).optionKey || "").startsWith(
        "expedite"
      )
  ).length;
  // PIN #3 (Michael 2026-07-19 — supersedes the 7/18 quote-default ruling):
  // must-choose stays everywhere EXCEPT when a "we'll contact you" quote
  // option is present — then pre-select the CHEAPEST real-priced option.
  // The quote never defaults while a priced option exists; quote-only lists
  // keep current behavior (single option, pre-selected as before). The
  // oversized+messenger auto-select (dc370cf) is reproduced by this rule —
  // the messenger is the only priced option there.
  const quoteOptionPresent = deliveryOptions.some(
    option => !option.disabled && option.isQuote
  );
  const pricedSelectableOptions = deliveryOptions.filter(
    option =>
      !option.disabled &&
      !option.isQuote &&
      option.value !== shippingPickupOption.value
  );
  const cheapestPricedOption = pricedSelectableOptions.length
    ? pricedSelectableOptions.reduce((cheapest, option) => {
        const parsePrice = (price: string) =>
          Number(String(price || "").replace(/[^0-9.]/g, ""));
        return parsePrice(option.price) < parsePrice(cheapest.price)
          ? option
          : cheapest;
      })
    : null;
  const preselectCheapestPriced = quoteOptionPresent && !!cheapestPricedOption;
  const mustChooseDelivery =
    selectableDeliveryCount >= 2 &&
    !hasUserChosenDelivery &&
    !preselectCheapestPriced;

  // Re-ask whenever the destination or delivery context changes — new zip or
  // ship-type means new options, so a previous click no longer counts.
  useEffect(() => {
    setHasUserChosenDelivery(false);
  }, [
    data.shippingZipcode,
    data.deliveryZipcode,
    data.shippingCountry,
    data.deliveryCountry,
    data.shippingType,
  ]);

  // Oversized + messenger available: the messenger IS the delivery option
  // (Michael 7/18) — select it so the customer is charged the real rate
  // instead of the $0 arranged-delivery marker.
  useEffect(() => {
    if (data.shippingType !== "oversized") return;
    const messenger = shippingOptions.find(
      option => option.label === "manhattan-messenger"
    );
    if (!messenger || messenger.value === data.shippingOption) return;
    updateShippingOption(messenger.value);
  }, [data.shippingType, shippingOptions, data.shippingOption]);

  // PIN #3: apply the pre-selection — with a quote option on the list and no
  // explicit customer choice yet, the cheapest priced option is the default
  // (the $0 quote marker never defaults while a priced option exists). An
  // explicit customer click — including on the quote option — always wins.
  useEffect(() => {
    if (!preselectCheapestPriced || !cheapestPricedOption) return;
    if (hasUserChosenDelivery) return;
    if (data.shippingOption === cheapestPricedOption.value) return;
    updateShippingOption(cheapestPricedOption.value);
  }, [
    preselectCheapestPriced,
    hasUserChosenDelivery,
    cheapestPricedOption?.value,
    data.shippingOption,
  ]);

  // 10-19: custom-only cart — select the custom marker rate (the only
  // non-pickup option) once the customer has picked a ship-to address.
  useEffect(() => {
    if (!customOnly || isPickup || isCalculatingShipping) return;
    const custom = shippingOptions.find(
      option => option.label === "custom"
    );
    if (!custom || custom.value === data.shippingOption) return;
    updateShippingOption(custom.value);
  }, [
    customOnly,
    isPickup,
    isCalculatingShipping,
    shippingOptions,
    data.shippingOption,
  ]);

  const getAvailableDeliveryMethods = () => {
    return {
      total: deliveryOptions.length,
      available: deliveryOptions.filter(option => !option.disabled),
      disabled: deliveryOptions.filter(option => option.disabled),
      availableNames: deliveryOptions
        .filter(option => !option.disabled)
        .map(option => option.title),
      disabledNames: deliveryOptions
        .filter(option => option.disabled)
        .map(option => option.title),
    };
  };

  if (typeof window !== "undefined") {
    (
      window as typeof window & {
        checkDeliveryOptions: typeof getAvailableDeliveryMethods;
      }
    ).checkDeliveryOptions = getAvailableDeliveryMethods;
  }

  const updateShippingOption = usePending(async function (
    value: ShippingOption["value"]
  ) {
    try {
      setIsUpdatingPricing(true);
      setData({ ...data, shippingOption: value });

      try {
        await checkout.updateShippingOption(value);
      } catch (updateError) {
        console.error("❌ Failed to update shipping option:", updateError);
      }
    } catch (error) {
      console.error("❌ updateShippingOption failed:", error);
    } finally {
      setIsUpdatingPricing(false);
    }
  });

  const isStudioArranged =
    data.shippingType === "oversized" ||
    data.shippingType === "multiple-calculated";
  const hasOtherAddressZipcode = Boolean(data.shippingZipcode?.trim());
  // F1 (2026-07-16): multi-piece carts still get a real, selectable Manhattan
  // Messenger quote for their delivery zip (the messenger pricer handles
  // multi-piece pricing) — only UPS remains contact-for-quote.
  const isMultipleCalculated = data.shippingType === "multiple-calculated";
  const hasMessengerOption = shippingOptions.some(
    option => option.label === "manhattan-messenger"
  );
  const hasDeliveryZipcode = Boolean(data.deliveryZipcode?.trim());
  const showMessengerForMultiple =
    isMultipleCalculated && hasDeliveryZipcode && hasMessengerOption;
  // 2026-07-18: same rule for the oversized (exceeds-table) state — the
  // messenger is a real option there (no UPS box ceiling) and was being
  // hidden entirely.
  const isOversized = data.shippingType === "oversized";
  const showMessengerForOversized =
    isOversized && hasDeliveryZipcode && hasMessengerOption;
  const shouldShowDeliveryOptions =
    data.shippingType !== "pickup" &&
    // 2026-07-24: never price a ship-to-my-billing destination we can't
    // ship to (billing is worldwide; shipping is US + CA).
    (data.shippingType !== "shipToMyAddress" || billingShippable) &&
    !destinationContradicted &&
    (!isOversized || showMessengerForOversized) &&
    (!isMultipleCalculated || showMessengerForMultiple) &&
    (data.shippingType !== "shipToOtherAddress" || hasOtherAddressZipcode);

  // F6: auto-reveal the delivery options once they finish loading, so the
  // (often cheaper) messenger / UPS choices aren't stranded below a scroll the
  // customer can't see. Fires once per appearance; resets when they hide.
  useEffect(() => {
    if (!shouldShowDeliveryOptions) {
      hasRevealedOptionsRef.current = false;
      return;
    }
    if (isCalculatingShipping) return;
    if (hasRevealedOptionsRef.current) return;
    hasRevealedOptionsRef.current = true;
    const timeoutId = setTimeout(() => {
      deliveryOptionsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
    return () => clearTimeout(timeoutId);
  }, [shouldShowDeliveryOptions, isCalculatingShipping]);

  const addressValues = isStudioArranged
    ? {
        firstName: data.deliveryFirstName,
        lastName: data.deliveryLastName,
        companyName: data.deliveryCompanyName,
        street: data.deliveryStreet,
        apartment: data.deliveryApartment,
        city: data.deliveryCity,
        state: data.deliveryState,
        zipcode: data.deliveryZipcode,
        country: data.deliveryCountry || "US",
      }
    : {
        firstName: data.shippingFirstName,
        lastName: data.shippingLastName,
        companyName: data.shippingCompanyName,
        street: data.shippingStreet,
        apartment: data.shippingApartment,
        city: data.shippingCity,
        state: data.shippingState,
        zipcode: data.shippingZipcode,
        country: data.shippingCountry || "US",
      };

  const setAddressData = (partial: Partial<typeof addressValues>) => {
    if (isStudioArranged) {
      setData({
        ...data,
        ...(partial.firstName !== undefined && {
          deliveryFirstName: partial.firstName,
        }),
        ...(partial.lastName !== undefined && {
          deliveryLastName: partial.lastName,
        }),
        ...(partial.companyName !== undefined && {
          deliveryCompanyName: partial.companyName,
        }),
        ...(partial.street !== undefined && { deliveryStreet: partial.street }),
        ...(partial.apartment !== undefined && {
          deliveryApartment: partial.apartment,
        }),
        ...(partial.city !== undefined && { deliveryCity: partial.city }),
        ...(partial.state !== undefined && { deliveryState: partial.state }),
        ...(partial.zipcode !== undefined && {
          deliveryZipcode: partial.zipcode,
        }),
        ...(partial.country !== undefined && {
          deliveryCountry: partial.country,
        }),
      });
    } else {
      setData({
        ...data,
        ...(partial.firstName !== undefined && {
          shippingFirstName: partial.firstName,
        }),
        ...(partial.lastName !== undefined && {
          shippingLastName: partial.lastName,
        }),
        ...(partial.companyName !== undefined && {
          shippingCompanyName: partial.companyName,
        }),
        ...(partial.street !== undefined && { shippingStreet: partial.street }),
        ...(partial.apartment !== undefined && {
          shippingApartment: partial.apartment,
        }),
        ...(partial.city !== undefined && { shippingCity: partial.city }),
        ...(partial.state !== undefined && { shippingState: partial.state }),
        ...(partial.zipcode !== undefined && {
          shippingZipcode: partial.zipcode,
        }),
        ...(partial.country !== undefined && {
          shippingCountry: partial.country,
        }),
      });
    }
  };

  // Phase B: which postal system the visible address form uses, and
  // whether the ACTIVE destination is Canadian (drives the DDU note).
  const addressIsCa = (addressValues.country || "US") === "CA";
  const activeDestIsCa =
    data.shippingType === "shipToMyAddress"
      ? (data.billingCountry || "US") === "CA"
      : data.shippingType === "pickup"
        ? false
        : addressIsCa;

  // Destination whose deliverability matters: the address the box actually
  // goes to (billing fields for ship-to-my-address, delivery fields for
  // studio-arranged, shipping fields otherwise). Pickup/custom: none.
  const validationDestination = () => {
    if (skipShipping || data.shippingType === "pickup") return null;
    if (isStudioArranged) {
      return {
        street: data.deliveryStreet,
        apartment: data.deliveryApartment,
        city: data.deliveryCity,
        state: data.deliveryState,
        zipcode: data.deliveryZipcode,
        country: data.deliveryCountry || "US",
        target: "delivery" as const,
      };
    }
    if (data.shippingType === "shipToOtherAddress") {
      return {
        street: data.shippingStreet,
        apartment: data.shippingApartment,
        city: data.shippingCity,
        state: data.shippingState,
        zipcode: data.shippingZipcode,
        country: data.shippingCountry || "US",
        target: "shipping" as const,
      };
    }
    return {
      street: data.billingStreet,
      apartment: data.billingApartment,
      city: data.billingCity,
      state: data.billingState,
      zipcode: data.billingZipcode,
      country: data.billingCountry || "US",
      target: "billing" as const,
    };
  };

  const applySuggestedAddress = () =>
    applySuggestion(addressReview?.suggested, addressReview?.target);

  const applySuggestion = (
    s: AddressCheck["suggested"] | undefined,
    target: AddressReviewState["target"] | undefined
  ) => {
    if (!s || !target) return;
    const suggestedCountry =
      String(s.country || "US").trim().toUpperCase() === "CA" ? "CA" : "US";
    // US ZIP input is type="number": apply ZIP5, not the +4 form. CA postal
    // codes apply WHOLE — substring(0,5) would mangle "M5V 2T6".
    const zip5 =
      suggestedCountry === "CA"
        ? String(s.zipcode || "")
        : String(s.zipcode || "").substring(0, 5);
    if (target === "billing") {
      setData({
        ...data,
        billingStreet: s.street,
        billingApartment: s.apartment || "",
        billingCity: s.city,
        billingState: s.state,
        billingZipcode: zip5,
        billingCountry: suggestedCountry,
      });
    } else {
      setAddressData({
        street: s.street,
        apartment: s.apartment || "",
        city: s.city,
        state: s.state,
        zipcode: zip5,
        country: suggestedCountry,
      });
    }
    approvedAddressKeyRef.current = addressKeyOf(
      s.street,
      s.apartment,
      s.city,
      s.state,
      zip5,
      suggestedCountry
    );
    setAddressReview(null);
    setAddressAttention(false);
    onNext();
  };

  const keepTypedAddress = () => {
    if (!addressReview) return;
    approvedAddressKeyRef.current = addressReview.key;
    setAddressReview(null);
    onNext();
  };

  // A stale suggestion must never outlive an edit to any address field.
  useEffect(() => {
    setAddressReview(null);
    setAddressAttention(false);
  }, [
    data.shippingStreet,
    data.shippingApartment,
    data.shippingCity,
    data.shippingState,
    data.shippingZipcode,
    data.shippingCountry,
    data.deliveryStreet,
    data.deliveryApartment,
    data.deliveryCity,
    data.deliveryState,
    data.deliveryZipcode,
    data.deliveryCountry,
    data.billingStreet,
    data.billingApartment,
    data.billingCity,
    data.billingState,
    data.billingZipcode,
    data.billingCountry,
    data.shippingType,
  ]);

  return (
    <form
      className="flex h-full flex-col"
      noValidate
      onSubmit={async e => {
        e.preventDefault();
        if (isValidatingAddress) return;
        // FIX 2026-07-23: these checks validated the BILLING fields even on
        // the ship-to-other flow — State=Hawaii + ZIP=98103 in the SHIPPING
        // form sailed through (7/23 probe). Validate the fields of the
        // address the box actually ships to.
        // Phase B: country-aware — CA validates province + postal format +
        // first-letter coherence; US unchanged.
        const destCountryForChecks =
          data.shippingType === "shipToOtherAddress"
            ? data.shippingCountry || "US"
            : data.billingCountry || "US";
        const stateForChecks =
          data.shippingType === "shipToOtherAddress"
            ? data.shippingState
            : data.billingState;
        const zipForChecks =
          data.shippingType === "shipToOtherAddress"
            ? data.shippingZipcode || ""
            : data.billingZipcode || "";
        const isValidState =
          destCountryForChecks === "CA"
            ? isValidCAProvince(stateForChecks)
            : isValidUSState(stateForChecks);
        const isValidZip =
          destCountryForChecks === "CA"
            ? isValidCAPostalFormat(zipForChecks) &&
              isValidPostalForProvince(zipForChecks, stateForChecks)
            : isValidZipForState(zipForChecks, stateForChecks);

        if (sentinelRef.current && !hasScrolledToBottom) {
          sentinelRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return;
        }

        if (skipShipping) {
          onNext();
          return;
        }

        if (!data.shippingType) {
          setValidationError("Please select a shipping option.");
          setShowTooltip(true);
          return;
        }
        // Phase B: billing country is worldwide (Stripe AVS verifies the
        // card), but we only SHIP to US + CA — a third-country billing
        // address can't be the destination.
        if (
          data.shippingType === "shipToMyAddress" &&
          !["US", "CA"].includes(
            String(data.billingCountry || "US").trim().toUpperCase()
          )
        ) {
          setValidationError(
            "We ship to US and Canadian addresses. Choose 'Ship To A Different Address' and enter one there."
          );
          setShowTooltip(true);
          return;
        }
        if (
          data.shippingType === "shipToOtherAddress" &&
          (!data.shippingFirstName ||
            !data.shippingLastName ||
            !data.shippingStreet ||
            !data.shippingCity ||
            !data.shippingZipcode ||
            !data.shippingState ||
            !isValidState ||
            !isValidZip)
        ) {
          setShowValidation(true);
          if (!isValidState) {
            setValidationError("Please select a valid state.");
          }
          if (!isValidZip) {
            setValidationError(
              "Please enter a valid ZIP code for the selected state."
            );
          } else {
            setValidationError("Please enter all required fields.");
          }
          setShowTooltip(true);
          return;
        }

        if (
          isStudioArranged &&
          (!data.deliveryFirstName ||
            !data.deliveryLastName ||
            !data.deliveryStreet ||
            !data.deliveryCity ||
            !data.deliveryState ||
            !data.deliveryZipcode)
        ) {
          setShowValidation(true);
          setValidationError(
            "Please enter all required delivery address fields."
          );
          setShowTooltip(true);
          return;
        }
        // FIX 2026-07-23: the delivery (studio-arranged) address previously
        // had presence checks only — no state/ZIP coherence at all.
        const deliveryIsCa = (data.deliveryCountry || "US") === "CA";
        if (
          isStudioArranged &&
          (deliveryIsCa
            ? !isValidCAProvince(data.deliveryState) ||
              !isValidCAPostalFormat(data.deliveryZipcode || "") ||
              !isValidPostalForProvince(
                data.deliveryZipcode || "",
                data.deliveryState
              )
            : !isValidUSState(data.deliveryState) ||
              !isValidZipForState(
                data.deliveryZipcode || "",
                data.deliveryState
              ))
        ) {
          setShowValidation(true);
          setValidationError(
            deliveryIsCa
              ? "Please check the delivery province and postal code — they don't match."
              : "Please check the delivery state and ZIP code — they don't match."
          );
          setShowTooltip(true);
          return;
        }

        if (shouldShowDeliveryOptions && mustChooseDelivery) {
          setValidationError("Please choose a delivery option.");
          setShowTooltip(true);
          deliveryOptionsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return;
        }

        // ── Phase A server validation (Decision 1: suggest-and-allow,
        // hard-block only a state/ZIP contradiction, fail-open on outage).
        // Runs once per distinct address; an approved address never
        // re-prompts on later submits.
        const dest = validationDestination();
        if (dest) {
          const key = addressKeyOf(
            dest.street,
            dest.apartment,
            dest.city,
            dest.state,
            dest.zipcode,
            dest.country
          );
          // The review card is already up for THIS address: the click is
          // on the wrong button. Flash the card, scroll to it, stop.
          if (addressReview && addressReview.key === key) {
            setAddressAttention(true);
            setValidationError("Please check the address below before continuing.");
            setShowTooltip(true);
            addressReviewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          if (approvedAddressKeyRef.current !== key) {
            setIsValidatingAddress(true);
            let proceed = false;
            try {
              const res = await Api.post<AddressCheck>(
                "/api/address-validation/validate",
                {
                  street: dest.street,
                  apartment: dest.apartment,
                  city: dest.city,
                  state: dest.state,
                  zipcode: dest.zipcode,
                  country: dest.country,
                }
              );
              const check = res.data;
              if (check.stateZipMismatch) {
                setShowValidation(true);
                setValidationError(
                  "The state and ZIP code don't match — please correct the address."
                );
                setShowTooltip(true);
              } else if (check.status === "corrected" && check.suggested) {
                const diffs = addressDiffs(dest, check.suggested);
                if (diffs.length === 0) {
                  // Cosmetic only — take the postal form and move on.
                  applySuggestion(check.suggested, dest.target);
                  return;
                }
                setAddressReview({ ...check, key, target: dest.target, diffs });
              } else if (check.status === "unverified") {
                setAddressReview({ ...check, key, target: dest.target });
              } else {
                // "verified", or "error" (fail-open — never block checkout
                // on the validator being down).
                approvedAddressKeyRef.current = key;
                proceed = true;
              }
            } catch {
              approvedAddressKeyRef.current = key; // fail-open
              proceed = true;
            } finally {
              setIsValidatingAddress(false);
            }
            if (!proceed) return;
          }
        }

        onNext();
      }}
    >
      <div className="flex h-full flex-col gap-5 px-4 pt-4 md:py-1">
        {!skipShipping && (
          <>
            {shippingOnly && (
              <p className="rounded border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
                Shipping for order <b>{shippingOnlyFor}</b> — your pieces are
                already paid for ($0.00 below). Enter the address and choose
                a shipping service.
              </p>
            )}
            <h3 className="text-lg font-bold leading-none text-[#636363]">
              Where is it going?
            </h3>
            <InputRadioGroup
              isTooltipEnabled={isPanelOpen}
              value={data.shippingType}
              onChange={value => {
                // FIX 2026-07-24: record explicit customer choices so the
                // latch/restore choreography can return to them.
                if (
                  value === "shipToMyAddress" ||
                  value === "shipToOtherAddress"
                ) {
                  lastExplicitShipToRef.current = value;
                  autoLatchedFromRef.current = null;
                }
                updateShippingType(value);
              }}
              options={
                // PIN #6: HI/AK chip with the approved destination copy —
                // same [chip, pickup] shape the internal oversized branch
                // builds, but with our label/tooltip.
                isHiAkArrangedDelivery
                  ? [
                      {
                        value: "oversized" as const,
                        label: "Hawaii/Alaska — delivery arranged after order",
                        tooltipText:
                          "Due to the destination, additional shipping charges apply. The studio will contact you to arrange delivery after your order is placed.",
                      },
                      ...shippingTypes.filter(type => type.value === "pickup"),
                    ]
                  : shippingOnly
                    ? shippingTypes.filter(type => type.value !== "pickup")
                    : shippingTypes
              }
              exceedsShippingLimits={
                exceedsShippingLimits && !isHiAkArrangedDelivery
              }
              hasMultipleNonRollableItems={
                hasMultipleCalculatedItems && !isHiAkArrangedDelivery
              }
            />

            <div className="flex flex-col gap-3">
              {data.shippingType === "pickup" ? (
                <AddressCard address={TRIBECA_ADDRESS} />
              ) : data.shippingType === "shipToMyAddress" ? (
                <>
                  <AddressCard
                    address={{
                      name: `${data.firstName} ${data.lastName}`,
                      address: {
                        line1: data.billingStreet,
                        line2: data.billingApartment || "",
                        city: data.billingCity,
                        state: data.billingState,
                        postal_code: data.billingZipcode,
                        country: data.billingCountry || "US",
                      },
                    }}
                  />
                  {/* 2026-07-24: say it up front, not just on submit. */}
                  {!billingShippable && (
                    <p className="text-sm font-semibold leading-snug text-[#C00000]">
                      We ship to US and Canadian addresses. Choose &quot;Ship
                      To A Different Address&quot; and enter one there.
                    </p>
                  )}
                </>
              ) : isStudioArranged ||
                data.shippingType === "shipToOtherAddress" ? (
                <>
                  <div className="flex items-center justify-between gap-1">
                    <p className="w-fit text-[18px] font-bold leading-none text-[#636363]">
                      {isStudioArranged
                        ? "Delivery Address"
                        : "Shipping Address"}
                    </p>
                    {isStudioArranged && (
                      /* FIX 2026-07-20 (PIN #13): pre-address, an oversized
                         cart rendered NO delivery options at all — reading as
                         "we can't ship this". Say up front that entering the
                         address may unlock a priced local delivery. */
                      <p className="mt-1 text-sm text-[#636363]">
                        UPS can't take this order. Enter the address — in the
                        NYC messenger area a messenger price appears right
                        here; elsewhere the studio will contact you with a
                        delivery quote after your order is placed.
                      </p>
                    )}
                  </div>
                  <div className="flex w-full gap-2">
                    {/* 2026-07-24: hidden for a non-US/CA billing country —
                        copying an unshippable address made a French postal
                        code price as a US ZIP. When shown, the country now
                        copies too (a CA billing address previously pasted
                        its province into the US-mode form). */}
                    {billingShippable &&
                      (ctx.userBillingAddress || data.billingStreet) && (
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => {
                            const saved = ctx.userBillingAddress as any;
                            setAddressData({
                              firstName: data.firstName || saved?.firstName,
                              lastName: data.lastName || saved?.lastName,
                              street:
                                data.billingStreet || saved?.address?.line1,
                              apartment:
                                data.billingApartment || saved?.address?.line2,
                              city: data.billingCity || saved?.address?.city,
                              state:
                                data.billingState || saved?.address?.state,
                              zipcode:
                                data.billingZipcode ||
                                saved?.address?.postal_code,
                              country:
                                billingCountryCode === "CA" ? "CA" : "US",
                            });
                          }}
                        >
                          Use My Billing Address
                        </Button>
                      )}
                    {ctx.userShippingAddress && (
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => {
                          const shipping = ctx.userShippingAddress as any;
                          setAddressData({
                            firstName:
                              shipping?.firstName || addressValues.firstName,
                            lastName:
                              shipping?.lastName || addressValues.lastName,
                            street:
                              shipping?.address?.line1 || addressValues.street,
                            apartment:
                              shipping?.address?.line2 ||
                              addressValues.apartment,
                            city: shipping?.address?.city || addressValues.city,
                            state:
                              shipping?.address?.state || addressValues.state,
                            zipcode:
                              shipping?.address?.postal_code ||
                              addressValues.zipcode,
                          });
                        }}
                      >
                        Use Saved Shipping Address
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 md:flex-nowrap">
                    <CheckoutTextInput
                      label="First Name"
                      name={
                        isStudioArranged
                          ? "deliveryFirstName"
                          : "shippingFirstName"
                      }
                      value={addressValues.firstName || ""}
                      onChange={value => setAddressData({ firstName: value })}
                      required
                      showValidation={showValidation}
                    />
                    <CheckoutTextInput
                      label="Last Name"
                      name={
                        isStudioArranged
                          ? "deliveryLastName"
                          : "shippingLastName"
                      }
                      value={addressValues.lastName || ""}
                      onChange={value => setAddressData({ lastName: value })}
                      required
                      showValidation={showValidation}
                    />
                    <CheckoutTextInput
                      label="Company (Optional)"
                      name={
                        isStudioArranged ? "deliveryCompany" : "shippingCompany"
                      }
                      value={addressValues.companyName || ""}
                      onChange={value => setAddressData({ companyName: value })}
                      flex={!isMobile}
                      className="md:w-unset w-full"
                    />
                  </div>
                  <div className="flex gap-3">
                    <CheckoutTextInput
                      label="Street"
                      name={
                        isStudioArranged ? "deliveryStreet" : "shippingStreet"
                      }
                      value={addressValues.street || ""}
                      onChange={value => setAddressData({ street: value })}
                      required
                      showValidation={showValidation}
                    />
                    <CheckoutTextInput
                      label="Apartment"
                      name={
                        isStudioArranged
                          ? "deliveryApartment"
                          : "shippingApartment"
                      }
                      value={addressValues.apartment || ""}
                      onChange={value => setAddressData({ apartment: value })}
                    />
                  </div>
                  {/* Phase B (2026-07-23): country picker — switching
                      clears state+code so a stale pair can't contradict the
                      new country's postal system. */}
                  <div className="flex gap-3">
                    <CheckoutSelect
                      label="Country"
                      name={
                        isStudioArranged ? "deliveryCountry" : "shippingCountry"
                      }
                      value={addressValues.country || "US"}
                      onChange={value => {
                        if (value !== (addressValues.country || "US")) {
                          setAddressData({
                            country: value,
                            state: "",
                            zipcode: "",
                          });
                        }
                      }}
                      data={SHIP_COUNTRIES}
                      required
                      showValidation={showValidation}
                    />
                  </div>
                  <div className="flex flex-wrap gap-3 md:flex-nowrap">
                    <CheckoutTextInput
                      label="City"
                      name={isStudioArranged ? "deliveryCity" : "shippingCity"}
                      value={addressValues.city || ""}
                      onChange={value => setAddressData({ city: value })}
                      required
                      showValidation={showValidation}
                      flex={!isMobile}
                      className="md:w-unset w-full"
                    />
                    <CheckoutSelect
                      label={addressIsCa ? "Province" : "State"}
                      name={
                        isStudioArranged ? "deliveryState" : "shippingState"
                      }
                      value={addressValues.state}
                      onChange={value => setAddressData({ state: value })}
                      data={addressIsCa ? CA_PROVINCES : US_STATES}
                      required
                      showValidation={showValidation}
                      customValidation={
                        addressIsCa ? isValidCAProvince : isValidUSState
                      }
                    />
                    <CheckoutTextInput
                      label={addressIsCa ? "Postal Code" : "ZIP Code"}
                      name={
                        isStudioArranged ? "deliveryZipcode" : "shippingZipcode"
                      }
                      type={addressIsCa ? "text" : "number"}
                      value={addressValues.zipcode || ""}
                      onChange={value => setAddressData({ zipcode: value })}
                      required
                      className="flex-1"
                      showValidation={showValidation}
                      // FIX 2026-07-23: validate against the state chosen in
                      // THIS form (shipping/delivery) — not the billing state.
                      // Phase B: CA validates postal-vs-province coherence.
                      customValidation={value =>
                        addressIsCa
                          ? isValidPostalForProvince(value, addressValues.state)
                          : isValidZipForState(value, addressValues.state)
                      }
                    />
                  </div>
                </>
              ) : null}
            </div>
            {/* Phase B: DDU disclosure — Canadian destination pays GST/HST
                + any brokerage at the door; no US sales tax charged. */}
            {activeDestIsCa && (
              <p className="text-sm leading-snug text-[#636363]">
                Canadian orders: no U.S. sales tax is charged. GST/HST and
                any customs/brokerage fees are billed to the recipient by
                the carrier at delivery.
              </p>
            )}
          </>
        )}

        {/* 2026-10-22 (T-51664_B, Michael): the studio-arranged box above
            only renders once a messenger option is back, so between the
            address and the rate (debounce + up to 8 s) NOTHING showed, and a
            zip outside the messenger zones showed nothing forever — a
            customer could read both as "can't ship". Name each state. */}
        {isStudioArranged && hasDeliveryZipcode && !shouldShowDeliveryOptions && (
          <div className="flex items-center gap-3 rounded-[10px] border border-solid border-[#BBDCDC] bg-[#F4FAFA] p-4">
            {ratesPending || isCalculatingShipping ? (
              <>
                <span
                  className="inline-block size-[18px] shrink-0 animate-spin rounded-full border-2 border-[#4AA0A0] border-t-transparent"
                  aria-hidden
                />
                <p className="text-sm leading-snug text-[#4a4a4a]">
                  Checking messenger delivery for{" "}
                  {String(data.deliveryZipcode || "").trim()}…
                </p>
              </>
            ) : (
              <p className="text-sm leading-snug text-[#4a4a4a]">
                No messenger delivery to{" "}
                {String(data.deliveryZipcode || "").trim()} —{" "}
                {shippingOnly
                  ? "contact the studio and we'll quote the shipping for this address."
                  : "the studio will contact you with a delivery quote after your order is placed."}
              </p>
            )}
          </div>
        )}
        {(skipShipping || shouldShowDeliveryOptions) && (
          <div
            className="flex flex-col gap-3 rounded-[10px] border border-solid border-[#BBDCDC] bg-[#F4FAFA] p-4"
            style={{ animation: "tpwDeliveryReveal 1.6s ease-out 1" }}
          >
            <style>{`@keyframes tpwDeliveryReveal{0%{box-shadow:0 0 0 4px rgba(74,160,160,.35)}100%{box-shadow:0 0 0 0 rgba(74,160,160,0)}}`}</style>
            <p
              ref={deliveryOptionsRef}
              className="text-[19px] font-extrabold leading-none text-[#191919]"
            >
              Choose your delivery
            </p>
            {!skipShipping && !customOnly && (
              <p className="-mt-1 text-[14px] leading-snug text-[#4a4a4a]">
                {mustChooseDelivery
                  ? "Your address unlocked these options — pick one to continue."
                  : "Delivery for your order:"}
              </p>
            )}
            {showMessengerForMultiple && (
              <p className="text-sm leading-snug text-[#636363]">
                Manhattan Messenger is available for your multi-piece order at
                the price below. Prefer UPS? Choose the quote option and
                we&apos;ll contact you with a shipping quote after your order
                is placed.
              </p>
            )}
            {skipShipping || customOnly ? (
              <div className="mb-2 flex h-[48px] items-center gap-3 rounded-[4px] border border-solid border-[#4AA0A0] bg-white p-3">
                <div className="flex size-[20px] shrink-0 items-center justify-center rounded-full border-2 border-[#4AA0A0] bg-[#4AA0A0]">
                  <IconCheck size={12} color="white" strokeWidth={3} />
                </div>
                <p className="text-[18px] font-bold leading-none text-[#191919]">
                  Custom
                </p>
              </div>
            ) : (
              <DescriptiveRadioGroup
                options={deliveryOptions}
                value={mustChooseDelivery ? "" : data.shippingOption}
                onChange={value => {
                  setHasUserChosenDelivery(true);
                  updateShippingOption(value);
                }}
                data={data}
                setData={setData}
                isLoading={isCalculatingShipping || ratesPending}
                shippingStatus={shippingStatus}
              />
            )}
          </div>
        )}
        {addressReview && (
          <div
            ref={addressReviewRef}
            className={`flex flex-col gap-2 rounded-[10px] border-solid bg-[#FFF9EC] p-4 ${
              addressAttention
                ? "border-2 border-[#C0392B]"
                : "border border-[#E6C567]"
            }`}
          >
            {addressReview.status === "corrected" && addressReview.suggested ? (
              <>
                <p className="text-[16px] font-bold leading-none text-[#191919]">
                  Confirm your address
                </p>
                <p className="text-sm leading-snug text-[#4a4a4a]">
                  The postal database has a different{" "}
                  {(addressReview.diffs || [])
                    .map(d => ({ street: "street number", state: "state", zip: "ZIP code" })[d])
                    .join(" and ")}{" "}
                  for this address — please check it:
                </p>
                <p className="text-sm leading-snug text-[#191919]">
                  <span className={addressReview.diffs?.includes("street") ? "font-bold text-[#C0392B]" : "font-bold"}>
                    {addressReview.suggested.street}
                  </span>
                  {addressReview.suggested.apartment
                    ? `, ${addressReview.suggested.apartment}`
                    : ""}
                  , {addressReview.suggested.city},{" "}
                  <span className={addressReview.diffs?.includes("state") ? "font-bold text-[#C0392B]" : "font-bold"}>
                    {addressReview.suggested.state}
                  </span>{" "}
                  <span className={addressReview.diffs?.includes("zip") ? "font-bold text-[#C0392B]" : "font-bold"}>
                    {String(addressReview.suggested.zipcode || "").substring(0, 5)}
                  </span>
                </p>
                {addressReview.messages.map(message => (
                  <p
                    key={message}
                    className="text-sm leading-snug text-[#8a6d1a]"
                  >
                    {message}
                  </p>
                ))}
                <div className="flex flex-wrap gap-2">
                  <Button size="xs" radius="xl" onClick={applySuggestedAddress}>
                    Use suggested address
                  </Button>
                  <Button
                    size="xs"
                    radius="xl"
                    variant="default"
                    onClick={keepTypedAddress}
                  >
                    Keep what I typed
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[16px] font-bold leading-none text-[#191919]">
                  We couldn&apos;t verify this address
                </p>
                {addressReview.messages.map(message => (
                  <p
                    key={message}
                    className="text-sm leading-snug text-[#8a6d1a]"
                  >
                    {message}
                  </p>
                ))}
                <p className="text-sm leading-snug text-[#4a4a4a]">
                  Double-check it, or continue if you&apos;re sure it&apos;s
                  right.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="xs"
                    radius="xl"
                    variant="default"
                    onClick={() => setAddressReview(null)}
                  >
                    Edit address
                  </Button>
                  <Button size="xs" radius="xl" onClick={keepTypedAddress}>
                    Use it anyway
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        <div ref={sentinelRef} className="h-px w-full" aria-hidden />
      </div>

      <div className="sticky bottom-0 z-[500] grid grid-cols-2 justify-between gap-3 rounded-b-xl border-t border-[#00000014] bg-white px-4 py-3 md:flex">
        <Button
          type="button"
          size="md"
          radius="xl"
          onClick={onPrev}
          w={isMobile ? "100%" : "120px"}
          h="40px"
          style={{ color: "#636363", backgroundColor: "#00000014" }}
        >
          Previous
        </Button>
        <Tooltip
          label={validationError}
          opened={showTooltip}
          position="top-end"
          withArrow
          arrowPosition="center"
          color="dark"
          multiline
          transitionProps={{ transition: "pop", duration: 300 }}
          zIndex={9999}
          styles={{
            tooltip: {
              minWidth: "200px",
              textAlign: "center",
            },
          }}
        >
          <Button
            type="submit"
            size="md"
            radius="xl"
            w={isMobile ? "100%" : "200px"}
            h="40px"
          >
            {isValidatingAddress
              ? "Checking address…"
              : hasScrolledToBottom
                ? "Save & Continue"
                : "Continue"}
          </Button>
        </Tooltip>
      </div>
    </form>
  );
};
